import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, ReplaySubject } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { E_Profile, Profile } from 'app/modules/profile/profile.model';
import { AuthUtils } from 'app/session/auth/auth.utils';
import { environment } from 'environments/environment';
import { apiMessageOptions } from 'app/shared/interceptors/api-message-context';
import { AUTH_STORAGE_KEYS } from 'app/session/auth/auth.storage';

interface SessionUserResponse {
    user?: Profile;
    organizations?: any;
    payload?: {
        user?: Profile;
        organizations?: any;
    };
}

@Injectable({
    providedIn: 'root'
})
export class ProfileService {
    private _profile: ReplaySubject<Profile> = new ReplaySubject<Profile>(1);
    private _organizations: ReplaySubject<any> = new ReplaySubject<any>(1);
    private _selectedOrganization: ReplaySubject<any> = new ReplaySubject<any>(1);
    private _selectedBranch: ReplaySubject<any> = new ReplaySubject<any>(1);
    protected _error: ReplaySubject<E_Profile> = new ReplaySubject<E_Profile>(1);

    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient) {
        // Load saved selections from localStorage
        this._loadSelections();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for user
     *
     * @param value
     */
    set user(value: Profile) {
        // Store the value
        this._profile.next(value);
    }

    get user$(): Observable<Profile> {
        return this._profile.asObservable();
    }
    get error$(): Observable<E_Profile> {
        return this._error.asObservable();
    }

    /**
     * Setter & getter for organizations
     *
     * @param value
     */
    set organizations(value: any) {
        // Store the value
        this._organizations.next(value);
    }

    get organizations$(): Observable<any> {
        return this._organizations.asObservable();
    }

    /**
     * Setter & getter for selected organization
     *
     * @param value
     */
    set selectedOrganization(value: any) {
        // Store in localStorage
        if (value) {
            localStorage.setItem(AUTH_STORAGE_KEYS.selectedOrganization, JSON.stringify(value));
        } else {
            localStorage.removeItem(AUTH_STORAGE_KEYS.selectedOrganization);
        }
        // Store the value
        this._selectedOrganization.next(value);
    }

    get selectedOrganization$(): Observable<any> {
        return this._selectedOrganization.asObservable();
    }

    /**
     * Setter & getter for selected branch
     *
     * @param value
     */
    set selectedBranch(value: any) {
        // Store in localStorage
        if (value) {
            localStorage.setItem(AUTH_STORAGE_KEYS.selectedBranch, JSON.stringify(value));
        } else {
            localStorage.removeItem(AUTH_STORAGE_KEYS.selectedBranch);
        }
        // Store the value
        this._selectedBranch.next(value);
    }

    get selectedBranch$(): Observable<any> {
        return this._selectedBranch.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Load current user
     */
    load(): Observable<Profile> {
        return this._httpClient.post<SessionUserResponse>(
            `${environment.api_url}/api/session/user`,
            {},
            apiMessageOptions(false)
        ).pipe(
            tap((response) => {
                const payload = response?.payload ?? response;
                const userPayload = this._resolveUserPayload(payload);
                const organizations = this._normalizeOrganizations(payload?.organizations);
                const normalizedUser = this._normalizeUser(userPayload, organizations);

                if (payload?.organizations) {
                    this._organizations.next(organizations);
                }

                if (normalizedUser) {
                    this._profile.next(normalizedUser);
                }
            }),
            map((response) => {
                const payload = response?.payload ?? response;
                const userPayload = this._resolveUserPayload(payload);
                const organizations = this._normalizeOrganizations(payload?.organizations);
                return this._normalizeUser(userPayload, organizations) as Profile;
            })
        );
    }

    /**
     * Set the user
     *
     * @param user
     */
    set(user: Profile): Observable<Profile> {
        return this._httpClient
            .post<Profile | E_Profile>(
                `${environment.api_url}/api/session/update`,
                user
            )
            .pipe(
                tap((response) => {
                    if (this._isErrorResponse(response)) {
                        this._error.next(response);
                        return;
                    }
                    // const nextUser = (response as SessionUserResponse)?.user || (response as User);
                    // const organizations = (response as SessionUserResponse)?.organizations;

                    // if (organizations) {
                    //     this._organizations.next(organizations);
                    // }
                    // if (nextUser) {
                    //     this._user.next(nextUser);
                    // }
                }),
                filter((item): item is Profile => this._isSuccessResponse(item)),
                // map((response) => (response as SessionUserResponse)?.user || (response as User))
            );
    }

    /**
     * Upload avatar image and return uploaded path
     */
    uploadAvatar(file: File, folder: string): Observable<string> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder', folder);

        return this._httpClient.post<any>(`${environment.api_url}/api/dashboard/upload`, formData).pipe(
            map((response) => {
                const path = response?.download;
                return path || '';
            })
        );
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Load selections from localStorage
     *
     * @private
     */
    private _loadSelections(): void {
        // Load selected organization
        const savedOrganization = localStorage.getItem(AUTH_STORAGE_KEYS.selectedOrganization);
        if (savedOrganization) {
            try {
                this._selectedOrganization.next(JSON.parse(savedOrganization));
            } catch (e) {
                localStorage.removeItem(AUTH_STORAGE_KEYS.selectedOrganization);
            }
        }

        // Load selected branch
        const savedBranch = localStorage.getItem(AUTH_STORAGE_KEYS.selectedBranch);
        if (savedBranch) {
            try {
                this._selectedBranch.next(JSON.parse(savedBranch));
            } catch (e) {
                localStorage.removeItem(AUTH_STORAGE_KEYS.selectedBranch);
            }
        }
    }


    private _normalizeUser(user: any, organizations: any[] = []): Profile | null {
        if (!user) {
            return null;
        }

        const roleSource = user?.role ?? user?.roles;
        const roleMap = this._extractRoleMap(roleSource) || this._extractRoleMap(user?.extra?.role_map);
        const selectedOrganization = this._getStoredSelection(AUTH_STORAGE_KEYS.selectedOrganization);
        const selectedBranch = this._getStoredSelection(AUTH_STORAGE_KEYS.selectedBranch);
        const resolvedName = `${user?.name || user?.fullname || user?.firstname || ''}`.trim();

        return {
            ...user,
            name: resolvedName || user?.name,
            fullname: user?.fullname || resolvedName || user?.name,
            othernames: user?.othernames || user?.firstname || '',
            role: AuthUtils.resolveEffectiveRoles(roleMap || roleSource, {
                selectedOrganizationCode: selectedOrganization?.code || null,
                selectedBranchCode: selectedBranch?.code || null,
                fallbackOrganizationCode: organizations?.[0]?.code || Object.keys(roleMap || {})[0] || null,
                user
            }),
            extra: {
                ...(user?.extra || {}),
                role_map: roleMap || user?.extra?.role_map || null
            }
        } as Profile;
    }

    private _resolveUserPayload(payload: any): any {
        if (!payload || typeof payload !== 'object') {
            return null;
        }

        if (payload.user && typeof payload.user === 'object') {
            return payload.user;
        }

        if (payload.id || payload.email || payload.fullname || payload.avatar) {
            return payload;
        }

        return null;
    }

    private _extractRoleMap(value: any): Record<string, any> | null {
        return value && typeof value === 'object' && !Array.isArray(value)
            ? value as Record<string, any>
            : null;
    }

    private _getStoredSelection(storageKey: string): any | null {
        const raw = localStorage.getItem(storageKey);

        if (!raw) {
            return null;
        }

        try {
            return JSON.parse(raw);
        } catch (e) {
            localStorage.removeItem(storageKey);
            return null;
        }
    }

    private _normalizeOrganizations(organizations: any): any[] {
        if (Array.isArray(organizations)) {
            return organizations.map((organization: any) => this._normalizeOrganization(organization));
        }

        if (organizations && typeof organizations === 'object') {
            return Object.values(organizations).map((organization: any) => this._normalizeOrganization(organization));
        }

        return [];
    }

    private _normalizeOrganization(organization: any): any {
        const branches = Array.isArray(organization?.branches)
            ? organization.branches
            : organization?.branches && typeof organization.branches === 'object'
                ? Object.values(organization.branches)
                : [];

        return {
            ...organization,
            branches
        };
    }

    protected _isErrorResponse(item: Profile | E_Profile | null | undefined): item is E_Profile {
        return !!item && typeof item === 'object' && 'error_id' in item;
    }

    protected _isSuccessResponse(item: Profile | E_Profile | null | undefined): item is Profile {
        return !!item && typeof item === 'object' && !('error_id' in item);
    }

}
